import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Chip from 'core-system/Chip'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import TableOperations from 'core-system/Table/TableOperationsUI'
import { FilterCheckbox, FilterObject } from 'core-system/Table/FilterModel'
import Text from 'core-system/Text'
import { DashboardPeriod, DashboardProgramEmployee } from '../dashboardTypes'

const Operations = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`

interface ProgramEmployeesTableProps {
  employees: DashboardProgramEmployee[]
  period?: DashboardPeriod
  isTransitPass?: boolean
  isLoading?: boolean
  merchants?: { id: string; label: string }[]
}

const formatMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={600}>
      {text}
    </Text>
  </TableRowCell>
)

const RowCell = ({ children }: { children: React.ReactNode }) => (
  <TableRowCell style={{ padding: '1.425rem' }} noWrap>
    <Text fontWeight={300} variant='action3'>
      {children}
    </Text>
  </TableRowCell>
)

const employeeName = (employee: DashboardProgramEmployee) => {
  const name = `${employee.firstName} ${employee.lastName}`.trim()
  return name || employee.email
}

const formatMemberStatus = (status: string | null | undefined) => {
  const trimmed = status?.trim()
  if (!trimmed) {
    return '—'
  }
  if (trimmed.toLowerCase() === 'replacement ordered') {
    return 'Ordered'
  }
  return trimmed
}

const formatBalance = (cents: number | null | undefined) => {
  if (cents == null) {
    return '—'
  }
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const merchantCents = (
  employee: DashboardProgramEmployee,
  period: DashboardPeriod,
  merchant: string
) =>
  employee.spendByMerchant?.[period]?.find((item) => item.merchant === merchant)
    ?.cents ?? 0

const statusLabel = (
  employee: DashboardProgramEmployee,
  isTransitPass: boolean
) => {
  if (isTransitPass) {
    return formatMemberStatus(employee.transitPass?.memberStatus)
  }
  return employee.cardStatus === 'active' ? 'Active' : 'Pending'
}

type EmployeeRow = DashboardProgramEmployee & {
  name: string
  activeMerchants: string[]
  statusLabel: string
}

const ProgramEmployeesTable = ({
  employees,
  period = 'all',
  isTransitPass = false,
  isLoading = false,
  merchants = [],
}: ProgramEmployeesTableProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<FilterCheckbox[]>([])
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  )

  const rows = useMemo<EmployeeRow[]>(
    () =>
      employees.map((employee) => ({
        ...employee,
        name: employeeName(employee),
        activeMerchants: merchants
          .filter((merchant) => merchantCents(employee, period, merchant.id) > 0)
          .map((merchant) => merchant.id),
        statusLabel: statusLabel(employee, isTransitPass),
      })),
    [employees, isTransitPass, merchants, period]
  )

  const filters = useMemo<FilterObject[]>(() => {
    const worksites = [
      ...new Set(rows.map((row) => row.worksite).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b))
    const sections: FilterObject[] = []
    if (worksites.length) {
      sections.push({
        header: 'Worksite',
        checkboxes: worksites.map((worksite) => ({
          label: worksite,
          checked: false,
          key: 'worksite',
          value: worksite,
        })),
      })
    }
    if (merchants.length) {
      sections.push({
        header: 'Merchant',
        checkboxes: merchants.map((merchant) => ({
          label: merchant.label,
          checked: false,
          key: 'activeMerchants',
          value: merchant.id,
        })),
      })
    } else {
      const statuses = [
        ...new Set(rows.map((row) => row.statusLabel).filter((label) => label !== '—')),
      ]
      if (statuses.length) {
        sections.push({
          header: 'Status',
          checkboxes: statuses.map((status) => ({
            label: status,
            checked: false,
            key: 'statusLabel',
            value: status,
          })),
        })
      }
    }
    return sections
  }, [merchants, rows])

  const visibleRows = useMemo(() => {
    const needle = appliedSearch.toLowerCase()
    let next = rows.filter((row) => {
      if (
        needle &&
        !['name', 'email', 'worksite'].some((key) =>
          String(row[key] ?? '')
            .toLowerCase()
            .includes(needle)
        )
      ) {
        return false
      }
      if (!selectedFilters.length) {
        return true
      }
      const grouped: Record<string, unknown[]> = {}
      selectedFilters.forEach((item) => {
        grouped[item.key] = [...(grouped[item.key] || []), item.value]
      })
      return Object.entries(grouped).every(([key, values]) => {
        const field = row[key as keyof EmployeeRow]
        if (Array.isArray(field)) {
          return values.every((value) => field.includes(value))
        }
        return values.includes(field)
      })
    })
    if (sortDirection) {
      next = [...next].sort((a, b) => {
        const result = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
        return sortDirection === 'asc' ? result : -result
      })
    }
    return next
  }, [appliedSearch, rows, selectedFilters, sortDirection])

  const applySearch = (value: string) => {
    setAppliedSearch(value.trim())
  }

  return (
    <>
      {employees.length ? (
        <Operations>
          <TableOperations
            handleSearch={applySearch}
            handleSearchClear={() => {
              setSearchValue('')
              setAppliedSearch('')
            }}
            onAscendingClick={() => setSortDirection('asc')}
            onDescendingClick={() => setSortDirection('desc')}
            filters={filters}
            handleApplyFilters={(nextFilters) => setSelectedFilters(nextFilters)}
            handleFiltersReset={() => setSelectedFilters([])}
            isFilterButtonActive={selectedFilters.length > 0}
            activeSortDirection={sortDirection}
            isSearchActive={Boolean(appliedSearch)}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Operations>
      ) : null}
      <Table
        headerCells={[
          <HeaderCell key='name' text='Name' />,
          <HeaderCell key='email' text='Email' />,
          <HeaderCell key='worksite' text='Worksite' />,
          ...(merchants.length
            ? merchants.map((merchant) => (
                <HeaderCell key={merchant.id} text={merchant.label} />
              ))
            : [
                <HeaderCell key='status' text='Status' />,
                ...(isTransitPass
                  ? [
                      <HeaderCell key='card' text='Card Number' />,
                      <HeaderCell key='balance' text='Balance' />,
                    ]
                  : []),
              ]),
          <HeaderCell key='spend' text={isTransitPass ? 'Topups' : 'Spend'} />,
        ]}
        rows={visibleRows.map((employee) => (
          <TableRow key={employee.id}>
            <RowCell>{employeeName(employee)}</RowCell>
            <RowCell>{employee.email || '—'}</RowCell>
            <TableRowCell
              style={{ padding: '1.425rem', maxWidth: '12rem', width: '12rem' }}
            >
              <Text
                fontWeight={300}
                variant='action3'
                hideOverflow
                maxWidth='12rem'
                title={employee.worksite || undefined}
              >
                {employee.worksite || '—'}
              </Text>
            </TableRowCell>
            {merchants.length ? (
              merchants.map((merchant) => (
                <RowCell key={merchant.id}>
                  {formatMoney(merchantCents(employee, period, merchant.id))}
                </RowCell>
              ))
            ) : (
              <>
                <TableRowCell style={{ padding: '1.425rem' }} noWrap>
                  <Chip
                    variant={
                      (isTransitPass
                        ? employee.transitPass?.memberStatus
                            ?.trim()
                            .toLowerCase() === 'active'
                        : employee.cardStatus === 'active')
                        ? 'green'
                        : 'grey'
                    }
                  >
                    {isTransitPass
                      ? formatMemberStatus(employee.transitPass?.memberStatus)
                      : employee.cardStatus === 'active'
                        ? 'Active'
                        : 'Pending'}
                  </Chip>
                </TableRowCell>
                {isTransitPass ? (
                  <>
                    <RowCell>{employee.transitPass?.cardNumber || '—'}</RowCell>
                    <RowCell>
                      {formatBalance(employee.transitPass?.balanceCents)}
                    </RowCell>
                  </>
                ) : null}
              </>
            )}
            <RowCell>
              {formatMoney(
                isTransitPass
                  ? (employee.transitPass?.topupsByPeriod?.[period] ?? 0)
                  : (employee.spendByPeriod?.[period] ?? 0)
              )}
            </RowCell>
          </TableRow>
        ))}
        maxRows={10}
        emptyMessage={
          employees.length ? 'No employees match' : 'No employees enrolled'
        }
        isLoading={isLoading}
        showPagination={visibleRows.length > 10}
      />
    </>
  )
}

export default React.memo(ProgramEmployeesTable)
